/**
 * ErrorBanner - Persistent error display with advanced details for support
 */

import { useState } from 'react';
import { useErrorStore, AppError } from '@/stores/errorStore';
import { AlertCircle, AlertTriangle, XCircle, X, RefreshCw, ChevronDown, ChevronUp, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { formatTraceForCopy, getTraceIdSuffix } from '@/core/trace/trace';

const severityConfig = {
  warning: {
    icon: AlertTriangle,
    bgClass: 'bg-amber-50 dark:bg-amber-950/50',
    borderClass: 'border-amber-200 dark:border-amber-800',
    iconClass: 'text-amber-600 dark:text-amber-400',
    textClass: 'text-amber-900 dark:text-amber-100',
  },
  error: {
    icon: AlertCircle,
    bgClass: 'bg-red-50 dark:bg-red-950/50',
    borderClass: 'border-red-200 dark:border-red-800',
    iconClass: 'text-red-600 dark:text-red-400',
    textClass: 'text-red-900 dark:text-red-100',
  },
  critical: {
    icon: XCircle,
    bgClass: 'bg-red-100 dark:bg-red-900/70',
    borderClass: 'border-red-300 dark:border-red-700',
    iconClass: 'text-red-700 dark:text-red-300',
    textClass: 'text-red-950 dark:text-red-50',
  },
};

interface ErrorItemProps {
  error: AppError;
  onClear: (id: string) => void;
}

function ErrorItem({ error, onClear }: ErrorItemProps) {
  const [showDetails, setShowDetails] = useState(false);
  const [copied, setCopied] = useState(false);
  
  const config = severityConfig[error.severity];
  const Icon = config.icon;
  const canDismiss = error.severity !== 'critical';
  
  const handleRetry = () => {
    if (error.retryAction) {
      error.retryAction();
      onClear(error.id);
    }
  };
  
  const handleCopy = async () => {
    const copyText = formatTraceForCopy(error.code, error.traceId, error.context);
    await navigator.clipboard.writeText(copyText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -20, height: 0 }}
      animate={{ opacity: 1, y: 0, height: 'auto' }}
      exit={{ opacity: 0, y: -20, height: 0 }}
      transition={{ duration: 0.2 }}
      className={cn('border-b', config.bgClass, config.borderClass)}
    >
      <div className="flex items-start gap-3 px-4 py-3">
        <Icon className={cn('h-5 w-5 flex-shrink-0 mt-0.5', config.iconClass)} />
        
        <div className="flex-1 min-w-0">
          <p className={cn('font-medium text-sm', config.textClass)}>{error.title}</p>
          <p className={cn('text-sm opacity-90 mt-0.5', config.textClass)}>{error.message}</p>
          
          {/* Toggle for advanced details */}
          <button
            onClick={() => setShowDetails(!showDetails)}
            className={cn('text-xs mt-2 flex items-center gap-1 opacity-70 hover:opacity-100', config.textClass)}
          >
            {showDetails ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            Lisätiedot
          </button>
        </div>
        
        <div className="flex items-center gap-2 flex-shrink-0">
          {error.retryAction && (
            <Button variant="ghost" size="sm" onClick={handleRetry}
              className={cn('h-8 px-3 text-xs', config.textClass, 'hover:bg-black/5 dark:hover:bg-white/10')}>
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
              Yritä uudelleen
            </Button>
          )}
          {canDismiss && (
            <Button variant="ghost" size="icon" onClick={() => onClear(error.id)}
              className={cn('h-7 w-7', config.textClass, 'hover:bg-black/5 dark:hover:bg-white/10')} aria-label="Sulje">
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
      
      {/* Advanced details panel */}
      <AnimatePresence>
        {showDetails && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="px-4 pb-3 pl-12"
          >
            <div className={cn('text-xs space-y-1 font-mono p-2 rounded bg-black/5 dark:bg-white/5', config.textClass)}>
              <div><span className="opacity-60">Virhekoodi:</span> {error.code}</div>
              <div><span className="opacity-60">Trace ID:</span> {getTraceIdSuffix(error.traceId)}</div>
              {error.context?.playerId && <div><span className="opacity-60">Pelaaja:</span> {String(error.context.playerId).slice(-8)}</div>}
              {error.context?.locationId && <div><span className="opacity-60">Sijainti:</span> {String(error.context.locationId).slice(-8)}</div>}
            </div>
            <Button variant="ghost" size="sm" onClick={handleCopy}
              className={cn('mt-2 h-7 text-xs', config.textClass)}>
              {copied ? <Check className="h-3 w-3 mr-1" /> : <Copy className="h-3 w-3 mr-1" />}
              {copied ? 'Kopioitu!' : 'Kopioi tiedot'}
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export function ErrorBanner() {
  const { errors, clearError } = useErrorStore();
  if (errors.length === 0) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-[100] pointer-events-none">
      <div className="pointer-events-auto">
        <AnimatePresence mode="sync">
          {errors.map((error) => <ErrorItem key={error.id} error={error} onClear={clearError} />)}
        </AnimatePresence>
      </div>
    </div>
  );
}

export default ErrorBanner;
